import type {
  LookupServiceProviderType,
  LookupServiceResponseType,
} from "@/types";
import type { LookupServiceInterface } from "@/interfaces/LookupServiceInterface";
import OperatorService from "@/services/OperatorService";
import { ProviderCallsignNotFoundError } from "@/utils/errors/LookupServiceErrors";
import { OperatorNotFoundError } from "@/utils/errors/OperatorErrors";

// Serves callsign lookups entirely from the local operator database (PouchDB +
// bundled static data). It never touches the network -- `requiresConnectivity`
// is false and `fetchByCallsign` is called with `allowNetwork: false` -- so when
// a user explicitly selects "Offline" the app behaves as if it were offline,
// regardless of actual connectivity.
class OfflineLookupService implements LookupServiceInterface {
  readonly providerName: LookupServiceProviderType = "OFFLINE";
  readonly requiresAuthentication: boolean = false;
  readonly requiresConnectivity: boolean = false;
  // Offline operator records include gridsquares.
  readonly providesGridsquares: boolean = true;
  sessionKey?: string;

  constructor(sessionKey: string = "") {
    this.sessionKey = sessionKey;
  }

  public async lookupCallsign(
    callsign: string,
    _sessionKey?: string // eslint-disable-line @typescript-eslint/no-unused-vars
  ): Promise<LookupServiceResponseType> {
    if (!callsign) {
      throw new Error("You must provide a callsign");
    }

    try {
      const operator = await OperatorService.fetchByCallsign(callsign, {
        allowNetwork: false,
      });
      // Tag the result with this provider so toasts/QSO attribution read
      // "Offline" rather than an empty provider.
      return { ...operator, provider: this.providerName };
    } catch (error) {
      // OperatorService throws OperatorNotFoundError on a genuine miss. Map only
      // that to the typed not-found error the UI surfaces as a toast (and
      // intentionally does not report to Sentry), matching the online adapters.
      // Anything else is an unexpected backend failure (e.g. a local DB read
      // error) and must propagate rather than masquerade as "not found".
      if (error instanceof OperatorNotFoundError) {
        throw new ProviderCallsignNotFoundError(
          this.providerName,
          "Callsign not found"
        );
      }
      throw error;
    }
  }
}

export default OfflineLookupService;
